import {
    getOwnedNodes,
    getInterfaceIdPrefix,
    InterfaceDescriptor,
    isFundamentalLikeNode,
    isMaterialLikeNode,
    isTemplateNode,
    MaterialLikeNode,
    TemplateGraph,
    TemplateInstance,
    TemplateParameterValue,
    ImageLikeNode,
    isImageLikeNode,
    isTemplateLikeNode,
    TemplateLikeNode,
} from "@cm/template-nodes"
import {ExtensionContext} from "@app/common/components/viewers/configurator/extensions/configurator-api-extension"

export type ParameterStorage = {
    storingInstance: TemplateInstance
    key: string
}

export function getParameterStorage(templateGraph: TemplateGraph, id: string): ParameterStorage {
    const [templateInstanceId, key] = getInterfaceIdPrefix(id)
    if (!templateInstanceId || !key) throw new Error(`Invalid id: ${id}`)
    return {storingInstance: getTemplateInstance(templateGraph, templateInstanceId), key}
}

export function getTemplateInstance(templateGraph: TemplateGraph, templateId: string): TemplateInstance {
    const templateInstance = getOwnedNodes(templateGraph).filter((x): x is TemplateInstance => x instanceof TemplateInstance && x.parameters.id === templateId)

    if (templateInstance.length !== 1) throw new Error(`Expected exactly one template instance with id ${templateId}, but found ${templateInstance.length}`)

    return templateInstance[0]
}

export type OriginWithStorage<T> = {value: T | undefined; setOnRoot: boolean}

export function getInputOrigin(descriptor: InterfaceDescriptor<unknown>, context: ExtensionContext): OriginWithStorage<TemplateParameterValue> {
    /*TODO: How to get the actual origin node here? The following problems exist:
    - Origin is a MaterialInput (inside the mattress)
    - When setting an overlay material color, origin is a MaterialReference */
    const [templateInstanceId, inputId] = getInterfaceIdPrefix(descriptor.props.id)
    if (!templateInstanceId || !inputId) throw new Error(`Invalid interface id ${descriptor.props.id}`)

    const templateInstance = getTemplateInstance(context.sceneManagerService.$templateGraph(), templateInstanceId)

    const result = templateInstance.parameters.parameters.parameters[inputId]
    if (result) return {value: result, setOnRoot: true}

    const origin = descriptor.props.origin
    return {value: origin && isFundamentalLikeNode(origin) ? origin : undefined, setOnRoot: false}
}

function getTypedOrigin<T>(
    descriptorOrId: InterfaceDescriptor<unknown> | string,
    context: ExtensionContext,
    typeGuard: (value: unknown) => value is T,
    label: string,
): OriginWithStorage<T> {
    const descriptor =
        typeof descriptorOrId === "string" ? context.sceneManagerService.$descriptorList().find((d) => d.props.id === descriptorOrId) : descriptorOrId
    if (!descriptor) return {value: undefined, setOnRoot: false}
    const {value, setOnRoot} = getInputOrigin(descriptor, context)
    if (!value) return {value: undefined, setOnRoot}
    if (isTemplateNode(value) && typeGuard(value)) return {value, setOnRoot}
    throw new Error(`Unsupported origin for ${label}: ${value}`)
}

export function getMaterialOrigin(descriptorOrId: InterfaceDescriptor<unknown> | string, context: ExtensionContext): OriginWithStorage<MaterialLikeNode> {
    return getTypedOrigin(descriptorOrId, context, isMaterialLikeNode, "material")
}

export function getImageOrigin(descriptorOrId: InterfaceDescriptor<unknown> | string, context: ExtensionContext): OriginWithStorage<ImageLikeNode> {
    return getTypedOrigin(descriptorOrId, context, isImageLikeNode, "image")
}

export function getTemplateOrigin(descriptorOrId: InterfaceDescriptor<unknown> | string, context: ExtensionContext): OriginWithStorage<TemplateLikeNode> {
    return getTypedOrigin(descriptorOrId, context, isTemplateLikeNode, "template")
}
